import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
    ArrowLeft, Users, Bookmark, CreditCard, Search, Crown, ChevronDown,
    UserPlus, Activity, Trash2, Calendar, DollarSign, Clock, Eye,
    ExternalLink, Loader2, ArrowUpDown, Sparkles, MessageCircleHeart,
    Shield, Smartphone,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ProBadge } from '../components/ProBadge';
import { FeedbackList } from '../components/admin/FeedbackList';
import { ReservedUsernamesList } from '../components/admin/ReservedUsernamesList';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = '/api';

const DATE_PRESETS = [
    { value: '', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_7_days', label: 'Last 7 days' },
    { value: 'last_30_days', label: 'Last 30 days' },
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'custom', label: 'Custom...' },
];

function formatDate(iso) {
    if (!iso) return '\u2014';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return '\u2014'; }
}

function formatDateTime(iso) {
    if (!iso) return '\u2014';
    try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return '\u2014'; }
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-primary', active, onClick }) {
    return (
        <div
            className={`rounded-xl border bg-card overflow-hidden cursor-pointer hover:bg-secondary/30 transition-all ${active ? 'border-primary ring-1 ring-primary/30' : 'border-border'}`}
            onClick={onClick}
            data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
            <div className="p-4 flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-secondary/60 ${color}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-2xl font-bold text-foreground" style={{ lineHeight: '1.2' }}>{value}</p>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
                </div>
            </div>
        </div>
    );
}

function StoreDownloadsPanel({ stats, onRefresh }) {
    const storeTotals = stats?.installations?.store_totals || {};
    const activated = {
        play_store: stats?.installations?.twa || 0,
        app_store: stats?.installations?.ios_pwa || 0,
    };

    return (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="store-downloads-panel">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Smartphone className="w-4 h-4 text-teal-500" />
                        Store Downloads
                    </h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                        Paste weekly totals from Play Console &amp; App Store Connect to see activation rate.
                    </p>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <StoreTotalRow
                    platform="play_store"
                    label="Play Store"
                    activatedLabel="Android installs"
                    activated={activated.play_store}
                    row={storeTotals.play_store}
                    onSaved={onRefresh}
                />
                <StoreTotalRow
                    platform="app_store"
                    label="App Store"
                    activatedLabel="iOS installs"
                    activated={activated.app_store}
                    row={storeTotals.app_store}
                    onSaved={onRefresh}
                />
            </div>
        </div>
    );
}

function StoreTotalRow({ platform, label, activatedLabel, activated, row, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(row?.total_installs ?? '');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setValue(row?.total_installs ?? '');
    }, [row?.total_installs]);

    const total = row?.total_installs;
    const hasTotal = typeof total === 'number' && total >= 0;
    const pct = hasTotal && total > 0 ? Math.round((activated / total) * 100) : null;
    const updatedAgo = row?.updated_at ? formatRelative(row.updated_at) : null;

    const save = async () => {
        const n = parseInt(value, 10);
        if (Number.isNaN(n) || n < 0) {
            toast.error('Enter a non-negative number');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(`${API}/admin/platform-installs/${platform}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ total_installs: n }),
            });
            if (res.ok) {
                toast.success(`${label} total saved`);
                setEditing(false);
                onSaved?.();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || 'Failed to save');
            }
        } catch {
            toast.error('Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rounded-lg border border-border bg-background/40 p-3" data-testid={`store-row-${platform}`}>
            <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-foreground">{label}</span>
                {!editing && (
                    <button
                        type="button"
                        onClick={() => setEditing(true)}
                        className="text-[11px] font-medium text-primary hover:underline"
                        data-testid={`edit-${platform}-btn`}
                    >
                        {hasTotal ? 'Edit' : 'Set total'}
                    </button>
                )}
            </div>
            {editing ? (
                <div className="mt-2 flex items-center gap-2">
                    <Input
                        type="number"
                        min="0"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        className="h-8 text-xs"
                        placeholder="e.g. 200"
                        data-testid={`input-${platform}`}
                    />
                    <Button size="sm" onClick={save} disabled={saving} data-testid={`save-${platform}-btn`}>
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setValue(row?.total_installs ?? ''); }} data-testid={`cancel-${platform}-btn`}>
                        Cancel
                    </Button>
                </div>
            ) : (
                <div className="mt-1.5">
                    <p className="text-xl font-bold text-foreground" style={{ lineHeight: '1.2' }} data-testid={`total-${platform}`}>
                        {hasTotal ? total.toLocaleString() : '—'}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                        Downloaded {updatedAgo ? `· updated ${updatedAgo}` : ''}
                    </p>
                    {hasTotal ? (
                        <div className="mt-1.5 text-[11px] text-muted-foreground" data-testid={`ratio-${platform}`}>
                            <span className="font-medium text-foreground">
                                {activated.toLocaleString()} / {total.toLocaleString()} activated
                            </span>
                            {pct !== null && (
                                <span className={`ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded font-semibold ${pct >= 50 ? 'bg-emerald-500/10 text-emerald-600' : pct >= 20 ? 'bg-amber-500/10 text-amber-600' : 'bg-rose-500/10 text-rose-600'}`}>
                                    {pct}%
                                </span>
                            )}
                            <span className="block text-[10px] mt-0.5">{activatedLabel} that launched the app</span>
                        </div>
                    ) : (
                        <p className="text-[10px] text-muted-foreground mt-1">Set store total to see activation rate.</p>
                    )}
                </div>
            )}
        </div>
    );
}

function formatRelative(iso) {
    try {
        const d = new Date(iso);
        const secs = Math.floor((Date.now() - d.getTime()) / 1000);
        if (secs < 60) return 'just now';
        const mins = Math.floor(secs / 60);
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 30) return `${days}d ago`;
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return null; }
}

function UserRow({ user, onExpand, expanded, onTierChange, onDelete, onOpenProfile }) {
    const [changingTier, setChangingTier] = useState(false);

    const handleTierChange = async (newTier) => {
        setChangingTier(true);
        try {
            const res = await fetch(`${API}/admin/users/${user.user_id}/tier`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ tier: newTier }),
            });
            if (res.ok) {
                toast.success(`${user.name || user.email} updated to ${newTier.replace('_', ' ')}`);
                onTierChange();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || 'Failed to update tier');
            }
        } catch {
            toast.error('Failed to update tier');
        } finally {
            setChangingTier(false);
        }
    };

    return (
        <div className="border-b border-border last:border-b-0" data-testid={`user-row-${user.user_id}`}>
            <div
                className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/30 cursor-pointer transition-colors"
                onClick={() => onExpand(user.user_id)}
            >
                {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full flex-shrink-0" referrerPolicy="no-referrer" />
                ) : (
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                        {(user.name || user.email || '?')[0].toUpperCase()}
                    </div>
                )}
                <div
                    className="flex-1 min-w-0 cursor-pointer group"
                    onClick={(e) => { e.stopPropagation(); onOpenProfile(user.user_id); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenProfile(user.user_id); } }}
                    data-testid={`open-user-profile-${user.user_id}`}
                    title="Open full profile"
                >
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate group-hover:text-primary group-hover:underline underline-offset-2">{user.name || 'No name'}</span>
                        <ProBadge tier={user.tier} />
                    </div>
                    <p className="text-xs text-muted-foreground truncate group-hover:text-primary/80 group-hover:underline underline-offset-2">{user.email}</p>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground flex-shrink-0">
                    <span className="flex items-center gap-1" title="Last visit"><Clock className="w-3 h-3" />{formatDate(user.last_login)}</span>
                    {user.payment && (
                        <span className="hidden sm:flex items-center gap-1 text-emerald-600" title={`Paid on ${formatDate(user.payment.date)}`}>
                            <DollarSign className="w-3 h-3" />${user.payment.amount}
                        </span>
                    )}
                    <span className="flex items-center gap-1"><Bookmark className="w-3 h-3" />{user.bookmark_count}</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </div>

            {expanded && (
                <div className="px-4 pb-4 pt-1 bg-secondary/20 border-t border-border/50">
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
                        <div>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Bookmarks</p>
                            <p className="text-sm font-semibold">{user.bookmark_count}</p>
                        </div>
                        <div>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Tier</p>
                            <p className="text-sm font-semibold capitalize">{user.tier?.replace('_', ' ') || 'Free'}</p>
                        </div>
                        <div>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Last Login</p>
                            <p className="text-sm font-semibold">{formatDateTime(user.last_login)}</p>
                        </div>
                        <div>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Joined</p>
                            <p className="text-sm font-semibold">{formatDate(user.created_at)}</p>
                        </div>
                        <div>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Last Payment</p>
                            {user.payment ? (
                                <p className="text-sm font-semibold text-emerald-600">${user.payment.amount} <span className="text-muted-foreground font-normal text-xs">on {formatDate(user.payment.date)}</span></p>
                            ) : (
                                <p className="text-sm text-muted-foreground">None</p>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <p className="text-xs text-muted-foreground mr-1">Tier:</p>
                        {['free', 'pro_200', 'pro_unlimited'].map((t) => (
                            <Button
                                key={t}
                                variant={user.tier === t ? 'default' : 'outline'}
                                size="sm"
                                className="h-7 text-xs rounded-full"
                                disabled={user.tier === t || changingTier}
                                onClick={(e) => { e.stopPropagation(); handleTierChange(t); }}
                                data-testid={`tier-btn-${t}-${user.user_id}`}
                            >
                                {t === 'free' ? 'Basic' : t === 'pro_200' ? 'Plus' : 'Pro'}
                            </Button>
                        ))}
                        <div className="flex-1" />
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 rounded-full"
                            onClick={(e) => { e.stopPropagation(); onDelete(user); }}
                            data-testid={`delete-user-${user.user_id}`}
                        >
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function BookmarkRow({ bookmark }) {
    const title = bookmark.title || 'Untitled';
    return (
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-b-0 hover:bg-secondary/20 transition-colors" data-testid={`bm-row-${bookmark.bookmark_id}`}>
            {bookmark.thumbnail ? (
                <img src={bookmark.thumbnail} alt="" className="w-12 h-8 rounded object-cover flex-shrink-0 bg-secondary" />
            ) : (
                <div className="w-12 h-8 rounded bg-secondary/60 flex items-center justify-center flex-shrink-0">
                    <Bookmark className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
            )}
            <div className="flex-1 min-w-0">
                {bookmark.url ? (
                    <a
                        href={bookmark.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-foreground truncate block hover:underline"
                        data-testid={`bm-title-link-${bookmark.bookmark_id}`}
                    >
                        {title}
                    </a>
                ) : (
                    <p className="text-sm font-medium text-foreground truncate">{title}</p>
                )}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {bookmark.user_name && <span>{bookmark.user_name}</span>}
                    <span>{formatDate(bookmark.created_at)}</span>
                    {bookmark.content_type && <span className="px-1.5 py-0.5 rounded bg-secondary text-[10px]">{bookmark.content_type}</span>}
                </div>
            </div>
        </div>
    );
}

const ADMIN_EMAILS = ['nimrod.lev@gmail.com', 'nimrod@brainsync.pro'];

export default function AdminPage() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!authLoading && (!user || !ADMIN_EMAILS.includes(user.email))) {
            navigate('/dashboard', { replace: true });
        }
    }, [user, authLoading, navigate]);

    if (authLoading || !user || !ADMIN_EMAILS.includes(user.email)) {
        return null;
    }

    return <AdminPageContent />;
}

function AdminPageContent() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [stats, setStats] = useState(null);
    // Unread feedback count: any row created that the admin hasn't
    // opened yet, PLUS rows where the user has sent an inbound reply
    // since the last admin action. Counts "needs attention", not just
    // brand-new rows.
    const [feedbackUnreadCount, setFeedbackUnreadCount] = useState(0);
    // Total reserved-username entries for the admin stat card.
    // Refreshed on tab open + after each mutation via the same list
    // endpoint the panel itself uses (single source of truth).
    const [reservedCount, setReservedCount] = useState(0);
    const [users, setUsers] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [bookmarks, setBookmarks] = useState([]);
    const [totalBookmarks, setTotalBookmarks] = useState(0);
    const [bmHasMore, setBmHasMore] = useState(true);
    const [bmLoading, setBmLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [tierFilter, setTierFilter] = useState('');
    const [datePreset, setDatePreset] = useState('');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [expandedUser, setExpandedUser] = useState(null);
    const [statFilter, setStatFilter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [sortBy, setSortBy] = useState('created_at_desc');
    const bmSentinelRef = useRef(null);
    const PAGE_SIZE = 20;

    // Sort options are exposed via `sortBy` state and applied server-side
    // in `fetchUsers` below; no client-side sort helper is needed.

    // Sort is now server-side (see `fetchUsers`) so every page shares
    // the same global ordering. `sortedUsers` is kept as an alias for
    // the JSX below and to preserve any lingering callers.
    const sortedUsers = users;

    // Reset to page 0 whenever the sort changes so the user immediately
    // sees the new top of the sorted set (not their old page offset).
    useEffect(() => { setPage(0); }, [sortBy]);

    const dateParams = useCallback(() => {
        const p = new URLSearchParams();
        if (datePreset && datePreset !== 'custom') {
            p.set('date_preset', datePreset);
        } else if (datePreset === 'custom') {
            if (customFrom) p.set('date_from', new Date(customFrom).toISOString());
            if (customTo) p.set('date_to', new Date(customTo + 'T23:59:59').toISOString());
        }
        // Ship the caller's local TZ offset so backend `_parse_date_range`
        // aligns "today" / "yesterday" / "this_week" / … to the admin's
        // calendar day instead of the UTC day. `getTimezoneOffset()`
        // returns minutes WEST of UTC (Israel UTC+3 → -180). Included on
        // every request so the value survives the URL and can be logged.
        // Reported production Aug 26 2026 — see PRD.
        try {
            p.set('tz_offset_minutes', String(new Date().getTimezoneOffset()));
        } catch (_) { /* fall back to UTC when the JS engine has no TZ data */ }
        return p.toString();
    }, [datePreset, customFrom, customTo]);

    const fetchStats = useCallback(async () => {
        try {
            const dp = dateParams();
            const res = await fetch(`${API}/admin/stats${dp ? '?' + dp : ''}`, { credentials: 'include' });
            if (res.status === 403) { navigate('/dashboard'); toast.error('Admin access required'); return; }
            if (res.ok) setStats(await res.json());
        } catch (err) { console.error('Failed to fetch admin stats:', err); }
    }, [navigate, dateParams]);

    const fetchUsers = useCallback(async () => {
        if (statFilter === 'bookmarks') return; // Don't fetch users when viewing bookmarks
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limit: String(PAGE_SIZE),
                skip: String(page * PAGE_SIZE),
                // Sort must be applied server-side so all pages share the
                // same ordering (not just the current page's slice).
                sort_by: sortBy,
            });
            if (search) params.set('search', search);
            if (tierFilter) params.set('tier', tierFilter);
            if (statFilter) params.set('filter', statFilter);
            if (datePreset && datePreset !== 'custom') params.set('date_preset', datePreset);
            else if (datePreset === 'custom') {
                if (customFrom) params.set('date_from', new Date(customFrom).toISOString());
                if (customTo) params.set('date_to', new Date(customTo + 'T23:59:59').toISOString());
            }
            // Include the admin's local TZ so the backend's "today"
            // window aligns to their calendar day, not UTC.
            try {
                params.set('tz_offset_minutes', String(new Date().getTimezoneOffset()));
            } catch (_) { /* fall back to UTC */ }
            const res = await fetch(`${API}/admin/users?${params}`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setUsers(data.users);
                setTotalUsers(data.total);
            }
        } catch (err) { console.error('Failed to fetch admin users:', err); }
        setLoading(false);
    }, [search, tierFilter, statFilter, page, datePreset, customFrom, customTo, sortBy]);

    const fetchBookmarks = useCallback(async (reset = false) => {
        if (statFilter !== 'bookmarks') return;
        setBmLoading(true);
        try {
            const skip = reset ? 0 : bookmarks.length;
            const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(skip) });
            if (datePreset && datePreset !== 'custom') params.set('date_preset', datePreset);
            else if (datePreset === 'custom') {
                if (customFrom) params.set('date_from', new Date(customFrom).toISOString());
                if (customTo) params.set('date_to', new Date(customTo + 'T23:59:59').toISOString());
            }
            try {
                params.set('tz_offset_minutes', String(new Date().getTimezoneOffset()));
            } catch (_) { /* fall back to UTC */ }
            const res = await fetch(`${API}/admin/bookmarks?${params}`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                if (reset) {
                    setBookmarks(data.bookmarks);
                } else {
                    setBookmarks(prev => [...prev, ...data.bookmarks]);
                }
                setTotalBookmarks(data.total);
                setBmHasMore(data.bookmarks.length === PAGE_SIZE);
            }
        } catch (err) { console.error('Failed to fetch admin bookmarks:', err); }
        setBmLoading(false);
    }, [statFilter, bookmarks.length, datePreset, customFrom, customTo]);

    useEffect(() => { fetchStats(); }, [fetchStats]);
    useEffect(() => { fetchUsers(); }, [fetchUsers]);
    useEffect(() => {
        if (statFilter === 'bookmarks') {
            fetchBookmarks(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statFilter, datePreset, customFrom, customTo]);

    // Infinite scroll for bookmarks
    useEffect(() => {
        if (statFilter !== 'bookmarks' || !bmHasMore || bmLoading) return;
        const sentinel = bmSentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(
            (entries) => { if (entries[0].isIntersecting) fetchBookmarks(false); },
            { rootMargin: '200px' }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [statFilter, bmHasMore, bmLoading, fetchBookmarks]);

    const handleDeleteUser = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const res = await fetch(`${API}/admin/users/${deleteTarget.user_id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(`Deleted ${deleteTarget.name || deleteTarget.email} (${data.counts.bookmarks} bookmarks removed)`);
                fetchUsers();
                fetchStats();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || 'Failed to delete user');
            }
        } catch {
            toast.error('Failed to delete user');
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(0);
        fetchUsers();
    };

    const toggleStat = (key) => {
        const newFilter = statFilter === key ? null : key;
        setStatFilter(newFilter);
        setPage(0);
        setTierFilter('');
    };

    const statFilterLabel = {
        users: 'All Users',
        active: 'Active Users',
        registered: 'Registered Users',
        visits: 'Visitors',
        bookmarks: 'Bookmarks',
        pro: 'Pro Users',
        revenue: null,
        feedback: 'Feedback',
        reserved: 'Reserved Usernames',
    };

    const showBookmarksList = statFilter === 'bookmarks';
    const showFeedbackList = statFilter === 'feedback';
    const showReservedList = statFilter === 'reserved';

    // Deep-link support: `/admin?tab=feedback` (used by the support
    // notification email) opens straight into the feedback view.
    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab === 'feedback' && statFilter !== 'feedback') {
            setStatFilter('feedback');
            setPage(0);
        }
    }, [searchParams]);

    // Reflect statFilter=feedback in the URL so the tab is bookmarkable.
    useEffect(() => {
        const current = searchParams.get('tab');
        if (statFilter === 'feedback' && current !== 'feedback') {
            const next = new URLSearchParams(searchParams);
            next.set('tab', 'feedback');
            setSearchParams(next, { replace: true });
        } else if (statFilter !== 'feedback' && current === 'feedback') {
            const next = new URLSearchParams(searchParams);
            next.delete('tab');
            setSearchParams(next, { replace: true });
        }
    }, [statFilter]);

    // Lightweight count of "unread" feedback for the stat card badge.
    // Includes both brand-new rows and rows where the user has sent
    // an inbound reply since the last admin action.
    useEffect(() => {
        let cancelled = false;
        fetch(`${API}/admin/feedback?limit=1`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (!cancelled && data) setFeedbackUnreadCount(data.counts?.unread || 0); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [statFilter]);

    // Reserved-username total for the stat card. Cheap - the list
    // endpoint returns a `total` alongside the first page even when
    // we ask for limit=1.
    useEffect(() => {
        let cancelled = false;
        fetch(`${API}/admin/reserved-usernames?limit=1`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (!cancelled && data) setReservedCount(data.total || 0); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [statFilter]);

    return (
        <div className="min-h-screen bg-background" data-testid="admin-page">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-sm border-b border-border">
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="h-8 w-8 p-0 rounded-full" data-testid="admin-back-btn">
                        <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <h1 className="text-lg font-bold text-foreground" style={{ lineHeight: '1.2' }}>Admin Dashboard</h1>
                    {process.env.REACT_APP_CLARITY_PROJECT_ID ? (
                        <a
                            href={`https://clarity.microsoft.com/projects/view/${process.env.REACT_APP_CLARITY_PROJECT_ID}/dashboard`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-transparent px-2.5 h-8 text-xs font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-500/10 transition-colors"
                            data-testid="admin-open-clarity-btn"
                            title="Open session recordings, heatmaps, and rage-click analysis in Microsoft Clarity"
                        >
                            <Eye className="w-3.5 h-3.5" />
                            Clarity
                            <ExternalLink className="w-3 h-3 opacity-60" />
                        </a>
                    ) : null}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/admin/curator')}
                        className={`${process.env.REACT_APP_CLARITY_PROJECT_ID ? '' : 'ml-auto'} gap-1.5 border-sky-500/40 text-sky-700 dark:text-sky-300 hover:bg-sky-500/10`}
                        data-testid="admin-open-curator-btn"
                    >
                        <Sparkles className="w-3.5 h-3.5" />
                        AI Curator
                    </Button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
                {/* Date Range Filter */}
                <div className="flex flex-wrap items-center gap-2" data-testid="date-range-filter">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <div className="flex flex-wrap gap-1">
                        {DATE_PRESETS.map((p) => (
                            <button
                                key={p.value}
                                onClick={() => { setDatePreset(p.value); setPage(0); setStatFilter(null); }}
                                className={`px-3 py-1 text-xs rounded-full border transition-colors ${datePreset === p.value ? 'bg-primary text-white border-primary' : 'bg-background text-muted-foreground border-border hover:bg-secondary'}`}
                                data-testid={`date-preset-${p.value || 'all'}`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    {datePreset === 'custom' && (
                        <div className="flex items-center gap-2 ml-2">
                            <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-7 text-xs w-36" data-testid="custom-date-from" />
                            <span className="text-xs text-muted-foreground">to</span>
                            <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-7 text-xs w-36" data-testid="custom-date-to" />
                        </div>
                    )}
                </div>

                {/* Stats Grid */}
                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-stats">
                        <StatCard icon={Users} label="Total Users" value={stats.total_users} color="text-blue-500" active={statFilter === 'users'} onClick={() => toggleStat('users')} />
                        <StatCard icon={Activity} label="Active" value={stats.active_users} color="text-emerald-500" active={statFilter === 'active'} onClick={() => toggleStat('active')} />
                        <StatCard icon={UserPlus} label="Registered" value={stats.registered} color="text-violet-500" active={statFilter === 'registered'} onClick={() => toggleStat('registered')} />
                        <StatCard icon={Eye} label="Visits" value={stats.visits} color="text-cyan-500" active={statFilter === 'visits'} onClick={() => toggleStat('visits')} />
                        <StatCard icon={Bookmark} label="Bookmarks" value={stats.total_bookmarks} color="text-amber-500" active={statFilter === 'bookmarks'} onClick={() => toggleStat('bookmarks')} />
                        <StatCard
                            icon={Crown}
                            label="Subscribed"
                            value={(stats.subscriptions?.pro_200 || 0) + (stats.subscriptions?.pro_unlimited || 0)}
                            sub={`${stats.subscriptions?.pro_200 || 0} Plus · ${stats.subscriptions?.pro_unlimited || 0} Pro`}
                            color="text-orange-500"
                            active={statFilter === 'pro'}
                            onClick={() => toggleStat('pro')}
                        />
                        <StatCard
                            icon={Smartphone}
                            label="App Installs"
                            value={stats.installations?.total || 0}
                            sub={`${stats.installations?.twa || 0} Android · ${stats.installations?.ios_pwa || 0} iOS · ${stats.installations?.web_pwa || 0} PWA`}
                            color="text-teal-500"
                        />
                        <StatCard icon={CreditCard} label="Revenue" value={`$${stats.revenue?.total || 0}`} sub={`${stats.revenue?.transactions || 0} txns`} color="text-emerald-600" active={statFilter === 'revenue'} onClick={() => toggleStat('revenue')} />
                        <StatCard icon={MessageCircleHeart} label="Feedback" value={feedbackUnreadCount} sub={feedbackUnreadCount === 1 ? '1 unread' : `${feedbackUnreadCount} unread`} color="text-rose-500" active={statFilter === 'feedback'} onClick={() => toggleStat('feedback')} />
                        <StatCard icon={Shield} label="Reserved" value={reservedCount} sub="Blocked usernames" color="text-indigo-500" active={statFilter === 'reserved'} onClick={() => toggleStat('reserved')} />
                    </div>
                )}

                {/* Store Downloads (Path B: manual entry) */}
                {stats && <StoreDownloadsPanel stats={stats} onRefresh={fetchStats} />}

                {/* Content Area - Users / Bookmarks / Feedback / Reserved */}
                {showReservedList ? (
                    <ReservedUsernamesList />
                ) : showFeedbackList ? (
                    <FeedbackList />
                ) : showBookmarksList ? (
                    /* Bookmarks List */
                    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="admin-bookmarks-list">
                        <div className="px-4 py-3 border-b border-border flex items-center gap-3">
                            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                <Bookmark className="w-4 h-4" />
                                Bookmarks ({totalBookmarks})
                                <button
                                    onClick={() => { setStatFilter(null); setPage(0); }}
                                    className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-secondary text-muted-foreground hover:bg-secondary/80"
                                    data-testid="clear-stat-filter"
                                >
                                    Clear filter
                                </button>
                            </h2>
                        </div>

                        {bookmarks.length === 0 && !bmLoading ? (
                            <div className="p-8 text-center text-sm text-muted-foreground">No bookmarks found</div>
                        ) : (
                            <>
                                {bookmarks.map((bm) => (
                                    <BookmarkRow key={bm.bookmark_id} bookmark={bm} />
                                ))}
                                {/* Infinite scroll sentinel */}
                                <div ref={bmSentinelRef} className="h-1" />
                                {bmLoading && (
                                    <div className="flex items-center justify-center py-4">
                                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    /* User Management */
                    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="admin-user-list">
                        <div className="px-4 py-3 border-b border-border flex flex-col sm:flex-row items-start sm:items-center gap-3">
                            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                {statFilter && statFilterLabel[statFilter] ? statFilterLabel[statFilter] : 'Users'} ({totalUsers})
                                {statFilter && (
                                    <button
                                        onClick={() => { setStatFilter(null); setPage(0); }}
                                        className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-secondary text-muted-foreground hover:bg-secondary/80"
                                        data-testid="clear-stat-filter"
                                    >
                                        Clear filter
                                    </button>
                                )}
                            </h2>
                            <form onSubmit={handleSearch} className="flex-1 flex items-center gap-2 w-full sm:w-auto">
                                <div className="relative flex-1 max-w-xs">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                    <Input
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Search by name or email..."
                                        className="h-8 pl-8 text-xs rounded-lg"
                                        data-testid="admin-search-input"
                                    />
                                </div>
                                <select
                                    value={tierFilter}
                                    onChange={(e) => { setTierFilter(e.target.value); setPage(0); }}
                                    className="h-8 px-2 text-xs border border-border rounded-lg bg-background text-foreground"
                                    data-testid="admin-tier-filter"
                                >
                                    <option value="">All tiers</option>
                                    <option value="free">Free</option>
                                    <option value="pro_200">Plus</option>
                                    <option value="pro_unlimited">Pro</option>
                                </select>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="h-8 px-2 text-xs border border-border rounded-lg bg-background text-foreground"
                                    data-testid="admin-sort-select"
                                >
                                    <option value="created_at_desc">Newest first</option>
                                    <option value="created_at_asc">Oldest first</option>
                                    <option value="name_asc">Name A-Z</option>
                                    <option value="name_desc">Name Z-A</option>
                                    <option value="bookmarks_desc">Most bookmarks</option>
                                    <option value="bookmarks_asc">Least bookmarks</option>
                                    <option value="last_login_desc">Recent visit</option>
                                    <option value="last_login_asc">Oldest visit</option>
                                </select>
                            </form>
                        </div>

                        {loading ? (
                            <div className="p-8 text-center text-sm text-muted-foreground">Loading users...</div>
                        ) : users.length === 0 ? (
                            <div className="p-8 text-center text-sm text-muted-foreground">No users found</div>
                        ) : (
                            <>
                                {sortedUsers.map((u) => (
                                    <UserRow
                                        key={u.user_id}
                                        user={u}
                                        expanded={expandedUser === u.user_id}
                                        onExpand={(id) => setExpandedUser(expandedUser === id ? null : id)}
                                        onTierChange={() => { fetchUsers(); fetchStats(); }}
                                        onDelete={(usr) => setDeleteTarget(usr)}
                                        onOpenProfile={(id) => navigate(`/admin/users/${id}`)}
                                    />
                                ))}
                                {totalUsers > PAGE_SIZE && (
                                    <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                                        <span className="text-xs text-muted-foreground">
                                            Showing {page * PAGE_SIZE + 1}\u2013{Math.min((page + 1) * PAGE_SIZE, totalUsers)} of {totalUsers}
                                        </span>
                                        <div className="flex gap-2">
                                            <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="admin-prev-page">
                                                Previous
                                            </Button>
                                            <Button variant="outline" size="sm" className="h-7 text-xs" disabled={(page + 1) * PAGE_SIZE >= totalUsers} onClick={() => setPage(p => p + 1)} data-testid="admin-next-page">
                                                Next
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </main>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                <AlertDialogContent data-testid="delete-user-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete User</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete <strong>{deleteTarget?.name || deleteTarget?.email}</strong> and all their data (bookmarks, collections, reminders, subscriptions). This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteUser}
                            disabled={deleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="confirm-delete-user"
                        >
                            {deleting ? 'Deleting...' : 'Delete User'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
